// Order- and material-level diff between two CPLEX versions' production plans.
//
// The per-network objective/orders chips answer "did it get better?"; this
// answers "what actually changed?" — which orders got newly filled or dropped,
// which orders moved to a different day, and where we're making more/less
// material on which day. It diffs the two versions' raw master_production_plan
// rows (result_tables.master_production_plan), the same rows the Sankey is built
// from. Pure → unit-tested.

import { stripCode } from './dailyView/mppHelpers';
import type { MPPRow } from './dailyView/types';

const DROPPED = 'Dropped / Unfilled';

export interface OrderChange {
  order: string;
  customer: string;
  au: string;
  lbs: number;
  fromDay: number | null;
  toDay: number | null;
}

export interface DayProduction {
  day: number;
  parentLbs: number;
  childLbs: number;
  delta: number;
}

export interface MaterialChange {
  item: string;
  parentLbs: number;
  childLbs: number;
  delta: number;
}

export interface PlanDiff {
  network: string;
  newlyFilled: OrderChange[];
  noLongerFilled: OrderChange[];
  movedDay: OrderChange[];
  productionByDay: DayProduction[];
  materialChanges: MaterialChange[];
  /** True when neither side had any plan rows for this network (nothing to diff). */
  empty: boolean;
}

interface OrderState {
  filled: boolean;
  lbs: number;
  dayLbs: Map<number, number>;
}

function networkRows(rows: readonly MPPRow[], network: string): MPPRow[] {
  return rows.filter((r) => r.model_type === network);
}

function lbsOf(r: MPPRow): number {
  return Number(r.s_to_o) || 0;
}

function orderStates(rows: readonly MPPRow[]): Map<string, OrderState> {
  const m = new Map<string, OrderState>();
  for (const r of rows) {
    const order = r.order ?? '';
    if (!order) continue;
    let s = m.get(order);
    if (!s) {
      s = { filled: false, lbs: 0, dayLbs: new Map() };
      m.set(order, s);
    }
    if (r.filled_from === DROPPED) continue; // unfilled marker contributes no fill
    s.filled = true;
    const lbs = lbsOf(r);
    s.lbs += lbs;
    if (r.day != null) s.dayLbs.set(r.day, (s.dayLbs.get(r.day) ?? 0) + lbs);
  }
  return m;
}

/** The day the order is mostly scheduled on (lbs-weighted), or null. */
function primaryDay(s: OrderState): number | null {
  let best: number | null = null;
  let bestV = -1;
  for (const [d, v] of s.dayLbs) {
    if (v > bestV) {
      bestV = v;
      best = d;
    }
  }
  return best;
}

function parseOrder(order: string): { customer: string; au: string } {
  const m = order.match(/^(.*)\s*\((AU\d+)\)\s*$/);
  return { customer: m ? m[1].trim() : order, au: m ? m[2] : '' };
}

function byLbsDesc(a: OrderChange, b: OrderChange): number {
  return b.lbs - a.lbs;
}

function dayLbsMap(rows: readonly MPPRow[]): Map<number, number> {
  const m = new Map<number, number>();
  for (const r of rows) {
    if (r.filled_from === DROPPED || r.day == null) continue;
    m.set(r.day, (m.get(r.day) ?? 0) + lbsOf(r));
  }
  return m;
}

function skuLbsMap(rows: readonly MPPRow[]): Map<string, number> {
  const m = new Map<string, number>();
  for (const r of rows) {
    if (r.filled_from === DROPPED) continue;
    const sku = stripCode(r.sku ?? '');
    if (!sku) continue;
    m.set(sku, (m.get(sku) ?? 0) + lbsOf(r));
  }
  return m;
}

/** Distinct network names (model_type) present in either version's rows. */
export function networksIn(...rowSets: readonly MPPRow[][]): string[] {
  const s = new Set<string>();
  for (const rows of rowSets) for (const r of rows) if (r.model_type) s.add(r.model_type);
  return [...s];
}

export function diffPlans(
  parentRows: readonly MPPRow[],
  childRows: readonly MPPRow[],
  network: string,
  materialLimit = 12,
): PlanDiff {
  const p = networkRows(parentRows, network);
  const c = networkRows(childRows, network);
  const empty = p.length === 0 && c.length === 0;

  const ps = orderStates(p);
  const cs = orderStates(c);
  const orders = new Set<string>([...ps.keys(), ...cs.keys()]);

  const newlyFilled: OrderChange[] = [];
  const noLongerFilled: OrderChange[] = [];
  const movedDay: OrderChange[] = [];
  for (const order of orders) {
    const a = ps.get(order);
    const b = cs.get(order);
    const pf = a?.filled ?? false;
    const cf = b?.filled ?? false;
    const { customer, au } = parseOrder(order);
    if (!pf && cf && b) {
      newlyFilled.push({ order, customer, au, lbs: b.lbs, fromDay: null, toDay: primaryDay(b) });
    } else if (pf && !cf && a) {
      noLongerFilled.push({ order, customer, au, lbs: a.lbs, fromDay: primaryDay(a), toDay: null });
    } else if (pf && cf && a && b) {
      const fromDay = primaryDay(a);
      const toDay = primaryDay(b);
      if (fromDay !== toDay) {
        movedDay.push({ order, customer, au, lbs: b.lbs, fromDay, toDay });
      }
    }
  }
  newlyFilled.sort(byLbsDesc);
  noLongerFilled.sort(byLbsDesc);
  movedDay.sort(byLbsDesc);

  // Production shifted across days (the leveling story).
  const pDay = dayLbsMap(p);
  const cDay = dayLbsMap(c);
  const days = [...new Set([...pDay.keys(), ...cDay.keys()])].sort((x, y) => x - y);
  const productionByDay: DayProduction[] = days.map((day) => {
    const parentLbs = pDay.get(day) ?? 0;
    const childLbs = cDay.get(day) ?? 0;
    return { day, parentLbs, childLbs, delta: childLbs - parentLbs };
  });

  // Material mix change by SKU (more of X, less of Y).
  const pSku = skuLbsMap(p);
  const cSku = skuLbsMap(c);
  const skus = new Set<string>([...pSku.keys(), ...cSku.keys()]);
  const materialChanges: MaterialChange[] = [];
  for (const item of skus) {
    const parentLbs = pSku.get(item) ?? 0;
    const childLbs = cSku.get(item) ?? 0;
    const delta = childLbs - parentLbs;
    if (Math.abs(delta) >= 0.5) materialChanges.push({ item, parentLbs, childLbs, delta });
  }
  materialChanges.sort((a, b) => Math.abs(b.delta) - Math.abs(a.delta));

  return {
    network,
    newlyFilled,
    noLongerFilled,
    movedDay,
    productionByDay,
    materialChanges: materialChanges.slice(0, materialLimit),
    empty,
  };
}
